import { ToolItem } from '../types';

export interface SeoMetadata {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogType: string;
  ogImage: string;
  jsonLd: Record<string, unknown>[];
}

const BASE_URL = 'https://dualistools.com';

export const DEFAULT_SEO: SeoMetadata = {
  title: 'Dualis Tools - 450+ Free Online Developer, PDF & Converter Tools',
  description: 'Access 450+ free online tools for PDF editing, image compression, developer utilities, calculators, text formatting, finance, and converters. 100% private & client-side.',
  keywords: 'free online tools, pdf tools, image compressor, json formatter, calculator, word counter, password generator, developer tools, unit converter, finance calculators',
  canonicalUrl: BASE_URL,
  ogTitle: 'Dualis Tools - 450+ Free Online Utilities & Productivity Tools',
  ogDescription: 'Instant, privacy-focused online tools for developers, designers, students, and professionals. Free forever, no registration required.',
  ogType: 'website',
  ogImage: `${BASE_URL}/og-banner.png`,
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Dualis Tools',
      'alternateName': ['Dualis Online Utilities', 'Dualis Developer & Productivity Tools'],
      'url': BASE_URL,
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': `${BASE_URL}/tools?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'Dualis Tools',
      'url': BASE_URL,
      'logo': `${BASE_URL}/logo.png`,
      'sameAs': [
        'https://twitter.com/dualistools',
        'https://github.com/dualistools'
      ]
    }
  ]
};

export const PAGE_SEO_MAP: Record<string, Partial<SeoMetadata>> = {
  'pdf-blank-page-remover': {
    title: 'PDF Blank Page Remover — Free & Fast Online Tool',
    description: 'Automatically scan, detect, and purge empty or nearly blank pages from PDF documents. Features adjustable sensitivity, confidence scoring, and instant side-by-side preview.',
    keywords: 'remove blank pages from pdf, delete blank pdf pages, purge empty pdf pages, blank page remover',
    canonicalUrl: `${BASE_URL}/pdf-blank-page-remover`
  },
  'home': {
    title: 'Dualis Tools - 450+ Free Online Developer, PDF & Utility Tools',
    description: 'Fast, secure, and private browser-based tools. Process PDFs, compress images, format JSON, calculate EMIs, convert units, and format text with 0ms server latency.',
    keywords: 'free web tools, pdf converter, image compressor online, json validator, online calculators, developer utilities',
    canonicalUrl: `${BASE_URL}/`
  },
  'all-tools': {
    title: 'All 450+ Free Online Tools Directory | Dualis Tools',
    description: 'Explore our complete index of 450+ online utilities categorized by PDF, Images, Text, Developer, Finance, Security, Data, and Calculators.',
    keywords: 'all online tools, web utilities directory, developer tools list, online pdf converters, free online software',
    canonicalUrl: `${BASE_URL}/tools`
  },
  'categories': {
    title: 'Tool Categories Hub - Browse 450+ Utilities | Dualis Tools',
    description: 'Browse all tool categories: PDF, Image, Developer, Text, Calculators, Finance, Security, Unit Converters, and Time & Date utilities.',
    keywords: 'tool categories, online utility hubs, developer tool categories, pdf tools list',
    canonicalUrl: `${BASE_URL}/categories`
  },
  'about': {
    title: 'About Dualis Tools - Privacy-First Web Utilities',
    description: 'Learn about Dualis Tools mission: providing blazing fast, privacy-focused, client-side web utilities for millions of users worldwide.',
    keywords: 'about dualis tools, private online tools, browser-based utilities',
    canonicalUrl: `${BASE_URL}/about`
  },
  'blog': {
    title: 'Blog & Developer Guides | Dualis Tools',
    description: 'Read the latest guides, web development tutorials, productivity tips, and tool updates from the Dualis Tools engineering team.',
    keywords: 'dualis blog, web development tips, pdf editing guide, json formatting tutorial',
    canonicalUrl: `${BASE_URL}/blog`
  },
  'blog/how-to-merge-pdf-files': {
    title: 'Merge PDF Files Online | Free, Private & Fast',
    description: 'Combine multiple PDF files online instantly. 100% free, secure browser-based processing, no file limits, and zero signups required. Merge your PDFs now.',
    keywords: 'how to merge pdf, combine pdf files on mac, combine pdf windows, merge pdf online free, merge pdf iphone, join pdfs',
    canonicalUrl: `${BASE_URL}/blog/how-to-merge-pdf-files`
  },
  'blog/how-to-extract-pdf-pages': {
    title: 'PDF Page Extractor | Free, Private & Lossless | Dualis Tools',
    description: 'Use our free PDF page extractor to pull specific pages securely. 100% private browser processing. Export as single files or ZIP archives instantly.',
    keywords: 'extract pdf pages, how to extract pages from pdf, pdf page extractor free, separate pdf pages private',
    canonicalUrl: `${BASE_URL}/blog/how-to-extract-pdf-pages`
  },
  'blog/how-to-split-pdf-files': {
    title: 'How to Split PDF into Separate Files | Free & Private | Dualis Tools',
    description: 'Learn how to split a PDF into separate files instantly. 100% free, private browser processing. Extract pages or split by range with zero server uploads.',
    keywords: 'split pdf into separate files, how to split pdf files, split pdf online free, separate pdf pages',
    canonicalUrl: `${BASE_URL}/blog/how-to-split-pdf-files`
  },
  'blog/how-to-rotate-pdf-pages': {
    title: 'How to Rotate PDF Online Free (and Save It Permanently) | Dualis Tools',
    description: 'Rotate PDF online free and save permanently. Select specific pages or rotate all 90° or 180°. 100% private browser processing with zero uploads.',
    keywords: 'how to rotate pdf, rotate pdf online free, rotate pdf and save permanently, rotate specific pages in pdf, rotate pdf mac',
    canonicalUrl: `${BASE_URL}/blog/how-to-rotate-pdf-pages`
  },
  'blog/remove-pages-from-pdf': {
    title: 'Remove Pages from PDF Securely | 100% Private Tool | Dualis Tools',
    description: 'Need to remove pages from PDF files? Delete PDF pages securely with our 100% local, in-browser tool. No cloud uploads. Try it free today!',
    keywords: 'remove pages from pdf, delete pdf pages securely, pdf page remover, remove pages from pdf private, delete pages from pdf free',
    canonicalUrl: `${BASE_URL}/blog/remove-pages-from-pdf`
  },
  'contact': {
    title: 'Contact Us & Tool Requests | Dualis Tools',
    description: 'Have a tool request or feedback? Get in touch with the Dualis Tools team and request new features or report issues.',
    keywords: 'contact dualis tools, request online tool, feedback',
    canonicalUrl: `${BASE_URL}/contact`
  },
  'pdf-tools': {
    title: 'Free PDF Tools & Converters Online - Merge, Compress, Edit | Dualis Tools',
    description: 'Complete suite of free PDF tools: Merge PDF, Split PDF, Compress PDF size, PDF to Word converter, Word to PDF, and page extractors.',
    keywords: 'pdf merger online, compress pdf free, pdf to docx, split pdf pages, pdf editor online',
    canonicalUrl: `${BASE_URL}/pdf-tools`
  },
  'pdf-merge': {
    title: 'Merge PDF Files Online — Free & Fast | Dualis Tools',
    description: 'Combine multiple PDF files into one document directly inside your browser. 100% private client-side merging with zero server uploads.',
    keywords: 'merge pdf, combine pdf, pdf combiner, join pdfs, pdf merger, stitch pdf, merge pdf free, combine pdf files on mac, merge pdf windows, merge pdf iphone, merge pdf android',
    canonicalUrl: `${BASE_URL}/pdf-merge`
  },
  'pdf-merger': {
    title: 'Merge PDF Files Online — Free & Fast | Dualis Tools',
    description: 'Combine multiple PDF files into one document directly inside your browser. 100% private client-side merging with zero server uploads.',
    keywords: 'merge pdf, combine pdf, pdf combiner, join pdfs, pdf merger, stitch pdf, merge pdf free, combine pdf files on mac, merge pdf windows, merge pdf iphone, merge pdf android',
    canonicalUrl: `${BASE_URL}/pdf-merge`
  },
  'pdf-split': {
    title: 'Split PDF Files Online — Free & Fast | Dualis Tools',
    description: 'Extract pages or split PDF documents into separate files directly inside your browser. 100% private client-side processing.',
    keywords: 'split pdf, pdf splitter, extract pdf pages, split pdf into separate files, split pdf online free, split pdf mac, split pdf windows, split pdf range',
    canonicalUrl: `${BASE_URL}/pdf-split`
  },
  'pdf-splitter': {
    title: 'Split PDF Files Online — Free & Fast | Dualis Tools',
    description: 'Extract pages or split PDF documents into separate files directly inside your browser. 100% private client-side processing.',
    keywords: 'split pdf, pdf splitter, extract pdf pages, split pdf into separate files, split pdf online free, split pdf mac, split pdf windows, split pdf range',
    canonicalUrl: `${BASE_URL}/pdf-split`
  },
  'split-pdf': {
    title: 'Split PDF Files Online — Free & Fast | Dualis Tools',
    description: 'Extract pages or split PDF documents into separate files directly inside your browser. 100% private client-side processing.',
    keywords: 'split pdf, pdf splitter, extract pdf pages, split pdf into separate files, split pdf online free, split pdf mac, split pdf windows, split pdf range',
    canonicalUrl: `${BASE_URL}/pdf-split`
  },
  'pdf-reorder-pages': {
    title: 'Reorder PDF Pages Online — Free & Fast | Dualis Tools',
    description: 'Drag and drop to rearrange pages in your PDF document effortlessly. 100% private client-side processing.',
    keywords: 'reorder pdf pages, pdf reorder, how to reorder pages in pdf, pdf page reorder, reorder pages in word, merge and reorder pdf, combine and reorder pdf, online pdf page reorder',
    canonicalUrl: `${BASE_URL}/pdf-reorder-pages`
  },
  'image-tools': {
    title: 'Free Image Tools & Converters - Compress, Resize, BG Remover | Dualis Tools',
    description: 'Optimize and edit images directly in your browser: Compress JPG/PNG/WebP, resize dimensions, remove background with AI, and crop photos.',
    keywords: 'image compressor, resize photo online, remove background ai, png compressor, webp converter',
    canonicalUrl: `${BASE_URL}/image-tools`
  },
  'text-tools': {
    title: 'Free Text Formatting & Analysis Tools - Word Counter, Case Converter | Dualis Tools',
    description: 'Analyze and format text instantly: Word and character counter, case converter (camelCase, snake_case), Lorem Ipsum generator, and text diff.',
    keywords: 'word counter online, case converter, character count, lorem ipsum generator, text formatter',
    canonicalUrl: `${BASE_URL}/text-tools`
  },
  'developer-tools': {
    title: 'Free Developer Tools - JSON Formatter, Base64, JWT Decoder | Dualis Tools',
    description: 'Essential developer utilities: JSON validator & prettifier, Base64 encoder/decoder, JWT decoder, Regex tester, and SQL formatter.',
    keywords: 'json formatter online, base64 decode, jwt decoder, json validator, sql formatter',
    canonicalUrl: `${BASE_URL}/developer-tools`
  },
  'color-tools': {
    title: 'Free Color Palette Tools & Converters - Picker, HEX to RGB, Contrast | Dualis Tools',
    description: 'Professional color utilities for designers & developers: Color picker, HEX/RGB/HSL converter, color palette generator, and WCAG contrast checker.',
    keywords: 'color picker online, hex to rgb, color contrast checker, palette generator, hsl converter',
    canonicalUrl: `${BASE_URL}/color-tools`
  },
  'security-tools': {
    title: 'Free Security & Encryption Tools - Password Generator, Hash Checker | Dualis Tools',
    description: 'Secure browser-based encryption utilities: Random password generator, MD5/SHA256 hash generator, string encrypter, and password strength analyzer.',
    keywords: 'password generator, sha256 hash online, md5 generator, password strength checker, secure encrypter',
    canonicalUrl: `${BASE_URL}/security-tools`
  },
  'social-tools': {
    title: 'Free Social Media Utilities - Bio Generators, Hashtags, YouTube Tools | Dualis Tools',
    description: 'Grow your social presence: Instagram bio generator, hashtag finder, YouTube thumbnail downloader, Tweet previewer, and link shorteners.',
    keywords: 'hashtag generator, youtube thumbnail downloader, instagram bio formatter, social media tools',
    canonicalUrl: `${BASE_URL}/social-tools`
  },
  'calculator-tools': {
    title: 'Free Online Calculators - Math, Percentage, Scientific & BMI | Dualis Tools',
    description: 'Precision calculators for everyday math: Percentage calculator, scientific calculator, BMI calculator, discount calculator, and age calculator.',
    keywords: 'percentage calculator, age calculator, bmi calculator online, scientific calculator, discount calculator',
    canonicalUrl: `${BASE_URL}/calculator-tools`
  },
  'file-tools': {
    title: 'Free File Converter & Extractor Tools - Zip, CSV, XML | Dualis Tools',
    description: 'Convert and manipulate files securely in browser: Zip/Unzip files, CSV to JSON converter, XML formatter, and file size calculators.',
    keywords: 'zip file extractor, csv to json, xml formatter online, file converter free',
    canonicalUrl: `${BASE_URL}/file-tools`
  },
  'data-tools': {
    title: 'Free Data Conversion & Processing Tools - JSON, CSV, YAML | Dualis Tools',
    description: 'Convert between structured data formats instantly: JSON to CSV, CSV to JSON, YAML to JSON, XML converter, and data deduplicator.',
    keywords: 'json to csv, yaml to json, data converter online, csv deduplicator',
    canonicalUrl: `${BASE_URL}/data-tools`
  },
  'productivity-tools': {
    title: 'Free Productivity Utilities - Pomodoro, Notes, QR Code Generator | Dualis Tools',
    description: 'Boost daily output: Custom QR Code generator, online Pomodoro timer, quick notepad, to-do list, and habit tracker.',
    keywords: 'qr code generator free, pomodoro timer online, quick notepad online, habit tracker',
    canonicalUrl: `${BASE_URL}/productivity-tools`
  },
  'unit-conversion': {
    title: 'Free Unit Converter Tools - Length, Weight, Temperature, Currency | Dualis Tools',
    description: 'Accurate unit conversion for length, weight, volume, speed, digital storage, pressure, and temperature measurements.',
    keywords: 'unit converter online, length converter, kg to lbs converter, temperature converter, digital storage conversion',
    canonicalUrl: `${BASE_URL}/unit-conversion`
  },
  'time-date-tools': {
    title: 'Free Time & Date Utilities - World Clock, Unix Timestamp, Stopwatch | Dualis Tools',
    description: 'Manage time effectively: Unix timestamp converter, world clock, timezone converter, stopwatch, countdown timer, and date calculator.',
    keywords: 'unix timestamp converter, date calculator online, world clock, timezone converter, countdown timer',
    canonicalUrl: `${BASE_URL}/time-date-tools`
  },
  'finance-tools': {
    title: 'Free Finance & Loan Calculators - EMI, ROI, Compound Interest | Dualis Tools',
    description: 'Make smart financial decisions: Home & Car loan EMI calculator, compound interest calculator, SIP calculator, ROI calculator, and salary tax estimator.',
    keywords: 'emi calculator, sip calculator online, compound interest calculator, loan amortisation, roi calculator',
    canonicalUrl: `${BASE_URL}/finance-tools`
  },
  'misc-tools': {
    title: 'Free Miscellaneous Utilities - Random Picker, Wheel Spinner | Dualis Tools',
    description: 'Fun & handy daily utilities: Random decision spinner, coin flipper, dice roller, barcode generator, and sound generators.',
    keywords: 'random name picker, wheel spinner online, coin flipper, barcode generator free',
    canonicalUrl: `${BASE_URL}/misc-tools`
  }
};

export function getSeoMetadata(nav: string = "", tool: ToolItem | null = null): SeoMetadata {
  nav = nav || "home";

  // If an active tool modal is open, generate a specialized tool-specific SEO head!
  if (tool) {
    const toolTitle = `${tool.name} - Free Online Tool | Dualis Tools`;
    const toolDesc = `${tool.description} Free, fast, private, and runs 100% in your browser with zero data uploads.`;
    const toolKeywords = `${tool.name.toLowerCase()}, ${tool.id}, free online ${tool.name.toLowerCase()}, ${tool.name.toLowerCase()} calculator, web utility`;
    const toolUrl = `${BASE_URL}/tool/${tool.id}`;

    return {
      title: toolTitle,
      description: toolDesc,
      keywords: toolKeywords,
      canonicalUrl: toolUrl,
      ogTitle: toolTitle,
      ogDescription: toolDesc,
      ogType: 'website',
      ogImage: `${BASE_URL}/og-banner.png`,
      jsonLd: [
        ...DEFAULT_SEO.jsonLd,
        {
          '@context': 'https://schema.org',
          '@type': ['SoftwareApplication', 'WebApplication'],
          'name': tool.name,
          'description': tool.description,
          'url': toolUrl,
          'applicationCategory': 'UtilitiesApplication',
          'operatingSystem': 'All modern web browsers (Chrome, Firefox, Safari, Edge)',
          'browserRequirements': 'Requires JavaScript. Requires HTML5.',
          'softwareVersion': '2.0',
          'featureList': [
            '100% Free online tool',
            'In-browser client-side processing',
            'Zero data logging or upload',
            'Instant calculation with 0ms server latency'
          ],
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          },
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': '4.9',
            'ratingCount': '1420',
            'bestRating': '5',
            'worstRating': '1'
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': 'Home',
              'item': BASE_URL
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': 'Tools Directory',
              'item': `${BASE_URL}/tools`
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': tool.name,
              'item': toolUrl
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': `Is ${tool.name} free to use?`,
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': `Yes, ${tool.name} on Dualis Tools is 100% free with no registration, software installation, or usage limits.`
              }
            },
            {
              '@type': 'Question',
              'name': `Is my data safe when using ${tool.name}?`,
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': `Absolutely. ${tool.name} runs entirely in your browser using local client-side processing. Your inputs and files are never saved or sent to any server.`
              }
            }
          ]
        }
      ]
    };
  }

  // Otherwise return page specific SEO
  const pageOverride = PAGE_SEO_MAP[nav] || {};
  const title = pageOverride.title || DEFAULT_SEO.title;
  const description = pageOverride.description || DEFAULT_SEO.description;
  const keywords = pageOverride.keywords || DEFAULT_SEO.keywords;
  const canonicalUrl = pageOverride.canonicalUrl || `${BASE_URL}/${nav === 'home' ? '' : nav}`;

  const faqList = nav.includes('-tools') ? [
    {
      '@type': 'Question',
      'name': 'Are these tools completely free to use?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Yes! All tools on Dualis Tools are 100% free with no subscription, sign-up, or hidden fees.'
      }
    },
    {
      '@type': 'Question',
      'name': 'Does Dualis Tools store my uploaded files or text?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'No. Dualis Tools processes files and text locally inside your web browser. Your data remains strictly confidential on your machine.'
      }
    }
  ] : [];

  return {
    title,
    description,
    keywords,
    canonicalUrl,
    ogTitle: title,
    ogDescription: description,
    ogType: 'website',
    ogImage: DEFAULT_SEO.ogImage,
    jsonLd: [
      ...DEFAULT_SEO.jsonLd,
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': 'Home',
            'item': BASE_URL
          },
          ...(nav !== 'home' ? [
            {
              '@type': 'ListItem',
              'position': 2,
              'name': nav.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              'item': canonicalUrl
            }
          ] : [])
        ]
      },
      ...(faqList.length > 0 ? [{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': faqList
      }] : [])
    ]
  };
}
